import { useEffect, useRef, useState } from "react";
import { ChatCircleDots, PaperPlaneRight, Microphone, X, BellRinging, Stop } from "@phosphor-icons/react";
import { assistantChat, assistantTranscribe, subscribeNews } from "../lib/api";
import { toast } from "sonner";

const LS_COUNT = "emmy_search_count";
const LS_SESSION = "emmy_session_id";
const LS_MSGS = "emmy_messages";
const LS_NEWS_DISMISSED = "emmy_news_dismissed";
const LS_AUTO_OPENED = "jimmy_auto_opened";
const UNLOCK_AT = 2;

const GREETING = {
  role: "assistant",
  text: "Hi, I'm Jimmy 👋 your NEET counselling buddy. Ask me about college fees, cutoffs, or say your rank and I'll suggest colleges. You can type or tap the mic to talk.",
};

export default function EmmyAssistant() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [newsDismissed, setNewsDismissed] = useState(() => localStorage.getItem(LS_NEWS_DISMISSED) === "1");
  const [subEmail, setSubEmail] = useState("");
  const [subBusy, setSubBusy] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [messages, setMessages] = useState(() => {
    try { const m = JSON.parse(localStorage.getItem(LS_MSGS) || "null"); return Array.isArray(m) && m.length ? m : [GREETING]; }
    catch { return [GREETING]; }
  });

  const sessionRef = useRef(localStorage.getItem(LS_SESSION) || null);
  const scrollRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  // The launcher is always visible. After the 2nd predictor search we auto-open
  // the chat ONCE (3s delay) so the student sees Jimmy + the subscribe prompt.
  useEffect(() => {
    const onSearch = () => {
      const n = (parseInt(localStorage.getItem(LS_COUNT) || "0", 10) || 0) + 1;
      localStorage.setItem(LS_COUNT, String(n));
      if (n >= UNLOCK_AT
          && localStorage.getItem(LS_AUTO_OPENED) !== "1"
          && window.location.pathname.includes("/predict")) {
        localStorage.setItem(LS_AUTO_OPENED, "1");
        setTimeout(() => setOpen(true), 3000);
      }
    };
    window.addEventListener("emmy-search", onSearch);
    return () => window.removeEventListener("emmy-search", onSearch);
  }, []);

  useEffect(() => { localStorage.setItem(LS_MSGS, JSON.stringify(messages.slice(-40))); }, [messages]);
  useEffect(() => {
    if (open && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setSending(true);
    try {
      const r = await assistantChat(sessionRef.current, msg);
      if (r.data.session_id && !sessionRef.current) {
        sessionRef.current = r.data.session_id;
        localStorage.setItem(LS_SESSION, r.data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I hit a snag. Please try again." }]);
    } finally { setSending(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 800) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "speech.webm");
          const r = await assistantTranscribe(fd);
          const text = (r.data.text || "").trim();
          if (text) await send(text);
          else toast.error("Didn't catch that — try again");
        } catch { toast.error("Could not transcribe audio"); }
        finally { setTranscribing(false); }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch { toast.error("Microphone access denied"); }
  };

  const stopRecording = () => {
    if (recorderRef.current && recording) recorderRef.current.stop();
    setRecording(false);
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!subEmail.trim() || subBusy) return;
    setSubBusy(true);
    try {
      const r = await subscribeNews(subEmail.trim(), "central");
      setSubscribed(true);
      if (r.data?.pending) toast.success("Check your inbox to confirm your subscription!");
      else toast.success("You're subscribed to the Central Newsroom!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe");
    } finally { setSubBusy(false); }
  };

  const dismissNews = () => {
    localStorage.setItem(LS_NEWS_DISMISSED, "1");
    setNewsDismissed(true);
  };

  return (
    <>
      {/* Floating rotating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="emmy-launcher"
          aria-label="Open Jimmy assistant"
          className="fixed bottom-5 right-5 z-[60] w-16 h-16 group"
        >
          <span className="absolute inset-0 rounded-full border-2 border-dashed border-[#6043EF]/50" style={{ animation: "emmySpin 8s linear infinite" }} />
          <span className="absolute inset-0 rounded-full bg-[#6043EF]/25" style={{ animation: "emmyPulse 2.2s ease-in-out infinite" }} />
          <span className="absolute inset-1 rounded-full bg-white shadow-lg overflow-hidden flex items-center justify-center border border-zinc-100 group-hover:scale-105 transition-transform">
            <img src="/mascot-badge.png" alt="Jimmy" className="w-full h-full object-cover scale-110" />
          </span>
          <span className="absolute -top-0.5 -right-0.5 w-6 h-6 rounded-full bg-[#6043EF] text-white flex items-center justify-center shadow-md">
            <ChatCircleDots size={14} weight="fill" />
          </span>
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div
          data-testid="emmy-panel"
          className="fixed bottom-5 right-5 z-[60] w-[92vw] max-w-[380px] h-[560px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-zinc-200 flex flex-col overflow-hidden"
          style={{ animation: "emmyPop 0.28s cubic-bezier(0.16,1,0.3,1)" }}
        >
          {/* Header */}
          <div className="bg-[#09090B] text-white px-4 py-3 flex items-center gap-3 shrink-0">
            <span className="w-9 h-9 rounded-full overflow-hidden border border-white/20 shrink-0">
              <img src="/mascot-badge.png" alt="Jimmy" className="w-full h-full object-cover scale-110" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold leading-none flex items-center gap-1.5">Jimmy
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" /></div>
              <div className="text-[11px] text-zinc-400 mt-0.5">NEET counselling assistant</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="emmy-close" aria-label="Close" className="text-zinc-400 hover:text-white transition-colors">
              <X size={18} weight="bold" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-[#FAFAFA]" data-testid="emmy-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`emmy-msg-${m.role}`}
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-[13px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#6043EF] text-white rounded-br-sm"
                      : "bg-white text-[#27272A] border border-zinc-200 rounded-bl-sm"
                  }`}
                >{m.text}</div>
              </div>
            ))}
            {(sending || transcribing) && (
              <div className="flex justify-start">
                <div className="bg-white border border-zinc-200 rounded-2xl rounded-bl-sm px-3 py-2.5 flex gap-1" data-testid="emmy-typing">
                  {[0, 1, 2].map((d) => (
                    <span key={d} className="w-1.5 h-1.5 rounded-full bg-zinc-400" style={{ animation: `emmyBlink 1.2s ${d * 0.2}s infinite` }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Newsroom subscribe prompt */}
          {!newsDismissed && !subscribed && (
            <div className="px-3 py-2.5 bg-violet-50 border-t border-violet-100 shrink-0" data-testid="emmy-news-prompt">
              <div className="flex items-start gap-2">
                <BellRinging size={16} weight="fill" className="text-[#6043EF] mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-semibold text-[#09090B] leading-tight">Never miss an official NEET notice</p>
                  <form onSubmit={subscribe} className="flex gap-1.5 mt-1.5">
                    <input
                      type="email" required value={subEmail} onChange={(e) => setSubEmail(e.target.value)}
                      placeholder="your email" data-testid="emmy-news-email"
                      className="flex-1 min-w-0 h-8 px-2.5 text-[12px] border border-violet-200 rounded-md focus:outline-none focus:border-[#6043EF] bg-white"
                    />
                    <button type="submit" disabled={subBusy} data-testid="emmy-news-subscribe"
                      className="h-8 px-3 text-[11px] font-bold uppercase tracking-wider bg-[#6043EF] text-white rounded-md disabled:opacity-60 hover:bg-[#4f35d4] transition-colors">
                      {subBusy ? "…" : "Get alerts"}
                    </button>
                  </form>
                </div>
                <button onClick={dismissNews} data-testid="emmy-news-dismiss" aria-label="Dismiss" className="text-violet-300 hover:text-violet-500 shrink-0">
                  <X size={14} weight="bold" />
                </button>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="px-3 py-2.5 border-t border-zinc-200 bg-white shrink-0">
            <div className="flex items-end gap-2">
              <button
                onClick={recording ? stopRecording : startRecording}
                disabled={transcribing || sending}
                data-testid="emmy-mic"
                aria-label={recording ? "Stop recording" : "Speak"}
                className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center transition-colors ${
                  recording ? "bg-rose-500 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                }`}
                title={recording ? "Tap to stop" : "Tap to speak"}
              >
                {recording ? <Stop size={16} weight="fill" /> : <Microphone size={17} weight="fill" />}
              </button>
              <textarea
                rows={1} value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={recording ? "Listening…" : "Ask about fees, cutoffs, your rank…"}
                data-testid="emmy-input"
                disabled={recording}
                className="flex-1 resize-none max-h-24 px-3 py-2 text-[13px] border border-zinc-300 rounded-xl focus:outline-none focus:border-[#6043EF] bg-white"
              />
              <button
                onClick={() => send()} disabled={!input.trim() || sending}
                data-testid="emmy-send" aria-label="Send"
                className="w-9 h-9 shrink-0 rounded-full bg-[#6043EF] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[#4f35d4] transition-colors"
              >
                <PaperPlaneRight size={16} weight="fill" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
